import { UNLIMITED_LIMIT_THRESHOLD, type Allowance } from "@signaloid/cloud-compute-engine-client";
import { CoreClass } from "@/types/api/cores";
import { TierCatalogEntry, TierLimits } from "@/types/api/tiers";
import type { UserResourceUsage } from "@/types/user";

export type CognitoAccountGroup =
	| "CustomerPortalUser"
	| "DeveloperTier"
	| "FreeTier"
	| "VirtualServerTier"
	| "ProTier"
	| "EnterpriseTierS"
	| "EnterpriseTierM"
	| "EnterpriseTierL"
	| "AcademicTier"
	| "NO_TIER";

export type AccountGroupCode =
	| "free-tier"
	| "developer-tier"
	| "academic-research-tier"
	| "pro-tier"
	| "enterprise-tier-s"
	| "enterprise-tier-m"
	| "enterprise-tier-l"
	| "virtual-server-tier";

export function compareTierCodes(tier0: AccountGroupCode, tier1: AccountGroupCode): boolean {
	const tierList: AccountGroupCode[] = [
		"free-tier",
		"developer-tier",
		"academic-research-tier",
		"pro-tier",
		"enterprise-tier-s",
		"enterprise-tier-m",
		"enterprise-tier-l",
		"virtual-server-tier",
	];

	return tierList.indexOf(tier0) >= tierList.indexOf(tier1);
}

export type TierFeatureHighlight = {
	text: string;
	icon: string;
	included?: boolean;
};

export const UserLimitsE = {
	ConcurrentTaskCount: "ConcurrentTaskCount",
	ConcurrentBuildCount: "ConcurrentBuildCount",
	CloudStorageBytes: "CloudStorageBytes",
	DataDriveCount: "DataDriveCount",
	BucketCount: "BucketCount",
	DataSourceCount: "DataSourceCount",
	PlotCount: "PlotCount",
	CoreCount: "CoreCount",
	RepositoryCount: "RepositoryCount",
	APIAccessCount: "APIAccessCount",
	KeyCount: "KeyCount",
	AllowedMicroArchitectures: "AllowedMicroArchitectures",
	AllowedCoreClasses: "AllowedCoreClasses",
	RegistryStorageBytesMaximum: "RegistryStorageBytesMaximum",
	ExecutionTimeInMillisecondsCount: "ExecutionTimeInMillisecondsCount",
	BuildTimeInMillisecondsCount: "BuildTimeInMillisecondsCount",
};
export type UserLimits = (typeof UserLimitsE)[keyof typeof UserLimitsE];

/**
 * UserLimits whose usage resets on a billing cycle (monthly quota or transient cap).
 * Limits NOT in this set are persistent capacity caps that only an upgrade can lift.
 */
export const RESETTING_LIMITS: ReadonlySet<UserLimits> = new Set<UserLimits>([
	UserLimitsE.ExecutionTimeInMillisecondsCount,
	UserLimitsE.ConcurrentTaskCount,
	UserLimitsE.ConcurrentBuildCount,
	UserLimitsE.PlotCount,
	UserLimitsE.APIAccessCount,
]);

/*
 * 'undefiled' allowance ==> "no limit"
 * 'null' allowance => "not allowed"
 */
export type TierAllowance = {
	ConcurrentTaskCount: number | null | undefined;
	ConcurrentBuildCount: number | null | undefined;
	CloudStorageBytes: number | null | undefined;
	DataDriveCount: number | null | undefined;
	BucketCount: number | null | undefined;
	DataSourceCount: number | null | undefined;
	PlotCount: number | null | undefined;
	CoreCount: number | null | undefined;
	RepositoryCount: number | null | undefined;
	MaximumAttachedProcessorRam: number | null | undefined;
	MaxPrecisionBits: number | null | undefined;
	MaxReferenceQuality: number | null | undefined;
	AllowedMicroArchitectures: string[];
	AllowedCoreClasses: CoreClass[];
	APIAccessCount: number | null | undefined;
	KeyCount: number | null | undefined;
	RegistryStorageBytesMaximum: number | null | undefined;
	ExecutionTimeInMillisecondsCount: number | null | undefined;
	BuildTimeInMillisecondsCount: number | null | undefined;
};

export type AccountTierDetail = {
	title: string;
	fullTitle: string;
	code: AccountGroupCode;
	monthlyModeSubtitles: string[];
	yearlyModeSubtitles: string[];
	shortVersionPoints: TierFeatureHighlight[];
	buttonText: string;
	requiresPayment: boolean;
	monthlySubscriptionStripePriceId?: string;
	yearlySubscriptionStripePriceId?: string;
	mustContactUs: boolean;
	legacyTier?: boolean;
};

export type ResolvedTierDetail = AccountTierDetail & { allowance: TierAllowance };

export const tiers: AccountTierDetail[] = [
	{
		title: "Free",
		fullTitle: "Free Tier",
		code: "free-tier",
		monthlyModeSubtitles: ["£0 per month"],
		yearlyModeSubtitles: ["£0 per year"],
		shortVersionPoints: [
			{
				text: "{ExecutionTimeHours} hours of task execution time per month.",
				icon: "checkmark",
			},
			{
				text: "{PlotCount} distribution plots per month.",
				icon: "checkmark",
			},
		],
		buttonText: "Continue with Free Tier",
		requiresPayment: false,
		mustContactUs: false,
	},
	{
		title: "Developer",
		fullTitle: "Developer Tier",
		code: "pro-tier",
		monthlyModeSubtitles: ["£49 per month"],
		yearlyModeSubtitles: ["£539 per year"],

		shortVersionPoints: [
			{
				text: "API access to Signaloid Compute Engine.",
				icon: "plus",
			},
			{
				text: "{APIAccessCount} API invocations per month.",
				icon: "plus",
			},
			{ text: "All the features of the Developer Tier.", icon: "checkmark" },
			// "API Usage Terms of Service.",
		],
		buttonText: "Subscribe",
		requiresPayment: true,
		monthlySubscriptionStripePriceId:
			import.meta.env.VITE_STRIPE_ENV === "live"
				? /* live price: */ "price_1ItrreB15W8cULvleOntFj45"
				: /* test price: */ "price_1IvNcBB15W8cULvlBtbi08qa",
		yearlySubscriptionStripePriceId:
			import.meta.env.VITE_STRIPE_ENV === "live"
				? /* live price: */ "price_1ObK8LB15W8cULvlnw5xjnbO"
				: /* test price: */ "price_1ObKDFB15W8cULvll1ykv7cF",
		mustContactUs: true,
	},
	{
		title: "Enterprise S",
		fullTitle: "Enterprise Cloud sized like dedicated AWS m7i.24xlarge",
		code: "enterprise-tier-s",
		monthlyModeSubtitles: ["Contact us for pricing"],
		yearlyModeSubtitles: ["Contact us for pricing"],
		shortVersionPoints: [
			{ text: "No upper limit on computation.", icon: "plus" },
			{ text: "Run {ConcurrentTaskCount} concurrent tasks.", icon: "plus" },
			{ text: "Access to all Signaloid Compute Engine cores.", icon: "plus" },
			{ text: "All the features of the Developer Tier.", icon: "checkmark" },
		],
		buttonText: "Contact Sales",
		requiresPayment: false,
		mustContactUs: true,
	},
	{
		title: "Enterprise M",
		fullTitle: "Enterprise Cloud sized like dedicated AWS m7i.48xlarge",
		code: "enterprise-tier-m",
		monthlyModeSubtitles: ["Contact us for pricing"],
		yearlyModeSubtitles: ["Contact us for pricing"],
		shortVersionPoints: [
			{ text: "No upper limit on computation.", icon: "plus" },
			{ text: "Run {ConcurrentTaskCount} concurrent tasks.", icon: "plus" },
			{ text: "Access to all Signaloid Compute Engine cores.", icon: "plus" },
			{ text: "All the features of the Enterprise S.", icon: "checkmark" },
		],
		buttonText: "Contact Sales",
		requiresPayment: false,
		mustContactUs: true,
	},
	{
		title: "Enterprise L",
		fullTitle: "Enterprise Cloud sized like dedicated AWS OR-OSG3N6V (5 m7i.48xlarge)",
		code: "enterprise-tier-l",
		monthlyModeSubtitles: ["Contact us for pricing"],
		yearlyModeSubtitles: ["Contact us for pricing"],
		shortVersionPoints: [
			{ text: "No upper limit on computation.", icon: "plus" },
			{ text: "Run {ConcurrentTaskCount} concurrent tasks.", icon: "plus" },
			{ text: "Access to all Signaloid Compute Engine cores.", icon: "plus" },
			{ text: "All the features of the Enterprise S.", icon: "checkmark" },
		],
		buttonText: "Contact Sales",
		requiresPayment: false,
		mustContactUs: true,
	},
	{
		title: "Developer",
		fullTitle: "Developer Tier",
		code: "developer-tier",
		monthlyModeSubtitles: [],
		yearlyModeSubtitles: [],
		shortVersionPoints: [],
		buttonText: "Subscribe",
		requiresPayment: true,
		mustContactUs: false,
		legacyTier: true,
	},
	{
		title: "Academic",
		fullTitle: "Academic Tier",
		code: "academic-research-tier",
		monthlyModeSubtitles: [],
		yearlyModeSubtitles: [],
		shortVersionPoints: [],
		buttonText: "Contact Us",
		requiresPayment: false,
		mustContactUs: true,
		legacyTier: true,
	},
];

/* Look up a code in tiers, never in listedTiers, or an account on a legacy tier stops resolving. */
export const listedTiers: AccountTierDetail[] = tiers.filter((tier) => tier.legacyTier !== true);

export const userGroupFromTokenPayload = (groups: null | CognitoAccountGroup[]) => {
	if (groups?.includes("VirtualServerTier")) {
		return "VirtualServerTier";
	}
	if (groups?.includes("EnterpriseTierL")) {
		return "EnterpriseTierL";
	}
	if (groups?.includes("EnterpriseTierM")) {
		return "EnterpriseTierM";
	}
	if (groups?.includes("EnterpriseTierS")) {
		return "EnterpriseTierS";
	}
	if (groups?.includes("ProTier")) {
		return "ProTier";
	}
	if (groups?.includes("DeveloperTier")) {
		return "DeveloperTier";
	}
	if (groups?.includes("AcademicTier")) {
		return "AcademicTier";
	}
	if (groups?.includes("FreeTier")) {
		return "FreeTier";
	}
	return "";
};

export const isUnlimitedLimit = (value: unknown): boolean =>
	typeof value === "number" && value >= UNLIMITED_LIMIT_THRESHOLD;

/* Catalog limits: null means "no limit", 0 means "not allowed", a missing key means "not allowed". */
const limitFromCatalog = (limit: number | null | undefined): number | null | undefined => {
	if (limit === undefined) {
		return null;
	}
	if (limit === null || isUnlimitedLimit(limit)) {
		return undefined;
	}

	return limit === 0 ? null : limit;
};

export const allowanceFromTierLimits = (limits: TierLimits, allowedCoreClasses: CoreClass[]): TierAllowance => {
	return {
		ConcurrentTaskCount: limitFromCatalog(limits.ConcurrentTaskCount),
		ConcurrentBuildCount: limitFromCatalog(limits.ConcurrentBuildCount),
		CloudStorageBytes: limitFromCatalog(limits.CloudStorageBytes),
		DataDriveCount: limitFromCatalog(limits.DataDriveCount),
		BucketCount: limitFromCatalog(limits.BucketCount),
		DataSourceCount: limitFromCatalog(limits.DataSourceCount),
		PlotCount: limitFromCatalog(limits.PlotCount),
		CoreCount: limitFromCatalog(limits.CoreCount),
		RepositoryCount: limitFromCatalog(limits.RepositoryCount),
		APIAccessCount: limitFromCatalog(limits.APIAccessCount),
		KeyCount: limitFromCatalog(limits.KeyCount),
		RegistryStorageBytesMaximum: limitFromCatalog(limits.RegistryStorageBytesMaximum),
		ExecutionTimeInMillisecondsCount: limitFromCatalog(limits.ExecutionTimeInMillisecondsCount),
		BuildTimeInMillisecondsCount: limitFromCatalog(limits.BuildTimeInMillisecondsCount),
		MaxPrecisionBits: limitFromCatalog(limits.CorePrecisionBits),
		MaxReferenceQuality: limitFromCatalog(limits.CoreReferenceQuality),
		MaximumAttachedProcessorRam: limitFromCatalog(limits.CoreMemorySizeBytesMaximum),
		AllowedMicroArchitectures: limits.CoreAvailableMicroarchitectures,
		AllowedCoreClasses: allowedCoreClasses,
	};
};

/* User record allowance: 0 means "not allowed", a missing key means "no limit", the inverse of the catalog. */
const limitFromUserRecord = (limit: number | undefined): number | null | undefined => {
	if (limit === undefined || isUnlimitedLimit(limit)) {
		return undefined;
	}

	return limit === 0 ? null : limit;
};

export const allowanceFromUserRecord = (
	served: Allowance,
	allowedCoreClasses: CoreClass[],
	allowedMicroarchitectures: string[],
): TierAllowance => {
	return {
		ConcurrentTaskCount: limitFromUserRecord(served.ConcurrentTaskCount),
		ConcurrentBuildCount: limitFromUserRecord(served.ConcurrentBuildCount),
		CloudStorageBytes: limitFromUserRecord(served.CloudStorageBytes),
		DataDriveCount: limitFromUserRecord(served.DataDriveCount),
		BucketCount: limitFromUserRecord(served.BucketCount),
		DataSourceCount: limitFromUserRecord(served.DataSourceCount),
		PlotCount: limitFromUserRecord(served.PlotCount),
		CoreCount: limitFromUserRecord(served.CoreCount),
		RepositoryCount: limitFromUserRecord(served.RepositoryCount),
		APIAccessCount: limitFromUserRecord(served.APIAccessCount),
		KeyCount: limitFromUserRecord(served.KeyCount),
		RegistryStorageBytesMaximum: limitFromUserRecord(served.RegistryStorageBytes),
		ExecutionTimeInMillisecondsCount: limitFromUserRecord(served.ExecutionTimeInMillisecondsCount),
		BuildTimeInMillisecondsCount: limitFromUserRecord(served.BuildTimeInMillisecondsCount),
		MaxPrecisionBits: limitFromUserRecord(served.MaxPrecisionBits),
		MaxReferenceQuality: limitFromUserRecord(served.MaxReferenceQuality),
		MaximumAttachedProcessorRam: limitFromUserRecord(served.MaximumAttachedProcessorRam),
		AllowedMicroArchitectures: allowedMicroarchitectures,
		AllowedCoreClasses: allowedCoreClasses,
	};
};

/* Served allowance keys, mapped or deliberately ignored. A key outside this set is reported as unknown. */
export const MAPPED_USER_RECORD_ALLOWANCE_KEYS: ReadonlySet<string> = new Set<string>([
	"DynamicInstructionCount",
	"ConcurrentTaskCount",
	"ConcurrentBuildCount",
	"CloudStorageBytes",
	"DataDriveCount",
	"BucketCount",
	"GatewayCount",
	"DataSourceCount",
	"PlotCount",
	"CoreCount",
	"RepositoryCount",
	"APIAccessCount",
	"KeyCount",
	"RegistryStorageBytes",
	"ExecutionTimeInMillisecondsCount",
	"BuildTimeInMillisecondsCount",
	"MaxPrecisionBits",
	"MaxReferenceQuality",
	"MaximumAttachedProcessorRam",
]);

export const thousandsSeparated = (value: number): string => value.toLocaleString("en-US");

const MILLISECONDS_PER_HOUR = 3_600_000;
const SMALLEST_SHOWN_HOURS = 0.01;

export const formatExecutionTimeHours = (milliseconds: number): string => {
	const hours = milliseconds / MILLISECONDS_PER_HOUR;
	if (hours > 0 && hours < SMALLEST_SHOWN_HOURS) {
		return `< ${SMALLEST_SHOWN_HOURS}`;
	}

	return hours.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

type TierPointFigure = {
	read: (allowance: TierAllowance) => number | null | undefined;
	format: (value: number) => string;
	unlimitedText?: string;
	nouns?: { plural: string; singular: string };
};

const TIER_POINT_FIGURES: Record<string, TierPointFigure | undefined> = {
	ExecutionTimeHours: {
		read: (allowance) => allowance.ExecutionTimeInMillisecondsCount,
		format: formatExecutionTimeHours,
		unlimitedText: "Unlimited task execution time per month.",
	},
	PlotCount: {
		read: (allowance) => allowance.PlotCount,
		format: thousandsSeparated,
		unlimitedText: "Unlimited distribution plots per month.",
	},
	ConcurrentTaskCount: {
		read: (allowance) => allowance.ConcurrentTaskCount,
		format: thousandsSeparated,
		unlimitedText: "Unlimited concurrent tasks.",
		nouns: { plural: "tasks", singular: "task" },
	},
	APIAccessCount: {
		read: (allowance) => allowance.APIAccessCount,
		format: thousandsSeparated,
		unlimitedText: "Unlimited API invocations per month.",
	},
};

const TIER_POINT_PLACEHOLDER_PATTERN = /\{(\w+)\}/g;

const resolveTierPoint = (point: TierFeatureHighlight, allowance: TierAllowance): TierFeatureHighlight | undefined => {
	const placeholders = [...point.text.matchAll(TIER_POINT_PLACEHOLDER_PATTERN)];
	if (placeholders.length === 0) {
		return point;
	}

	let text = point.text;

	for (const [placeholder, name] of placeholders) {
		const figure = TIER_POINT_FIGURES[name];
		if (figure === undefined) {
			return undefined;
		}

		const value = figure.read(allowance);

		if (value === null) {
			return undefined;
		}

		if (value === undefined) {
			return figure.unlimitedText === undefined ? undefined : { ...point, text: figure.unlimitedText };
		}

		text = text.replace(placeholder, figure.format(value));
		if (value === 1 && figure.nouns !== undefined) {
			text = text.replace(figure.nouns.plural, figure.nouns.singular);
		}
	}

	return { ...point, text };
};

export const resolveTierPoints = (points: TierFeatureHighlight[], allowance: TierAllowance): TierFeatureHighlight[] => {
	return points
		.map((point) => resolveTierPoint(point, allowance))
		.filter((point): point is TierFeatureHighlight => point !== undefined);
};

export const resolveTierDetail = (tier: AccountTierDetail, allowance: TierAllowance): ResolvedTierDetail => {
	return {
		...tier,
		allowance: allowance,
		shortVersionPoints: resolveTierPoints(tier.shortVersionPoints, allowance),
	};
};

export const withinLimit = (value: number, limit: number | null | undefined): boolean => {
	if (limit === undefined) {
		return true;
	}
	if (limit === null) {
		return false;
	}
	return value <= limit;
};

export const tierFromCode = (code: AccountGroupCode): AccountTierDetail | undefined => {
	return tiers.find((e) => e.code === code);
};

export const groupCode = (group: any | CognitoAccountGroup): AccountGroupCode | "" => {
	switch (group) {
		case "FreeTier":
			return "free-tier";
		case "AcademicTier":
			return "academic-research-tier";
		case "DeveloperTier":
			return "developer-tier";
		case "VirtualServerTier":
			return "virtual-server-tier";
		case "ProTier":
			return "pro-tier";
		case "EnterpriseTierS":
			return "enterprise-tier-s";
		case "EnterpriseTierM":
			return "enterprise-tier-m";
		case "EnterpriseTierL":
			return "enterprise-tier-l";
		default:
			return "";
	}
};

export const catalogEntryForTier = (
	catalog: undefined | TierCatalogEntry[],
	code: AccountGroupCode | "",
): TierCatalogEntry | undefined => {
	return catalog?.find((entry) => groupCode(entry.Tier) === code);
};

export type TierLimitDisplayObject = {
	name: string;
	id: UserLimits;
	usageId?: keyof UserResourceUsage;
	usage: number;
	limit: number;
	unit: string;
	quotaSuffix?: string | string[];
	format?: (value: number) => string;
};
